import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownward,
  ArrowUpward,
  CancelOutlined,
  LockOpen,
  LockOutlined,
  RemoveCircleOutline,
} from '@material-ui/icons';
import type { ContextAction } from 'Components/Organisms/GTable';
import useAlert from 'CustomHooks/useAlert';
import useToast from 'CustomHooks/useToast';
import { Severity } from 'Contexts/RestaurantContext';
import { apiMessage, OPEN_STATUSES, VisitWish, visitWishApi, VisitWishSettings } from './visitWishApi';
import { ExclusiveModal, liveOffer } from './ExclusiveOffers';
import { PriorityReach } from './wishFilters';

type Options = {
  restaurantId: string;
  enabled?: boolean;
  /** A day-scoped owner invalidates open dialogs and pending actions when its day changes. */
  scopeKey?: string;
  settings: VisitWishSettings | null;
  priorityOf: (wish: VisitWish) => PriorityReach;
  /** The wish as the API returns it after a stop or a priority change. */
  onUpdated: (wish: VisitWish) => void;
  /** Exclusive actions change wish and offer together; the owner loads again. */
  reload: () => unknown;
};

/**
 * The team's actions on one wish. The work list, the request sidebar and the
 * wish's own sidebar share them, so conditions, confirmations, errors and
 * reloads stay the same everywhere. One action runs at a time.
 */
export default function useWishActions({ restaurantId, enabled = true, scopeKey, settings, priorityOf, onUpdated, reload }: Options) {
  const toast = useToast();
  const confirm = useAlert();
  const scope = useMemo(() => ({}), [restaurantId, enabled, scopeKey]);
  const current = useRef(scope);
  current.current = scope;
  const [busy, setBusy] = useState<{ scope: object; wishId: string } | null>(null);
  const [markingExclusive, setMarkingExclusive] = useState<{ scope: object; wish: VisitWish } | null>(null);
  const running = useRef<{ scope: object } | null>(null);
  const mounted = useRef(false);

  useEffect(
    () => {
      mounted.current = true;
      return () => { mounted.current = false; };
    },
    []
  );

  const stillCurrent = () => enabled && mounted.current && current.current === scope;

  const guarded = async (wish: VisitWish, task: (isCurrent: () => boolean) => Promise<void>) => {
    if (!stillCurrent() || running.current?.scope === scope) return;
    const operation = { scope };
    running.current = operation;
    setBusy({ scope, wishId: wish.id });
    try {
      await task(stillCurrent);
    } catch (error) {
      if (stillCurrent()) toast(apiMessage(error, 'Action failed'), Severity.ERROR, 'reservations');
    } finally {
      if (running.current === operation) running.current = null;
      if (stillCurrent()) setBusy(null);
    }
  };

  const run = (wish: VisitWish, action: () => Promise<VisitWish>) =>
    guarded(wish, async (isCurrent) => {
      const updated = await action();
      if (isCurrent()) onUpdated(updated);
    });

  const runAndReload = (wish: VisitWish, action: () => Promise<unknown>) =>
    guarded(wish, async (isCurrent) => {
      await action();
      if (isCurrent()) await reload();
    });

  const priorityActions: ContextAction<VisitWish>[] = [
    {
      icon: <ArrowUpward color="inherit" />,
      iconColor: 'inherit',
      tooltipContent: 'Raise priority',
      tooltipContentTranslation: 'reservations',
      showCondition: (wish) => priorityOf(wish) !== 'none' && OPEN_STATUSES.includes(wish.status) && wish.priority < 10,
      onClick: (wish) => run(wish, () => visitWishApi.setPriority(restaurantId, wish.id, wish.priority + 1)),
    },
    {
      icon: <ArrowDownward color="inherit" />,
      iconColor: 'inherit',
      tooltipContent: 'Lower priority',
      tooltipContentTranslation: 'reservations',
      showCondition: (wish) => priorityOf(wish) !== 'none' && OPEN_STATUSES.includes(wish.status) && wish.priority > 0,
      onClick: (wish) => run(wish, () => visitWishApi.setPriority(restaurantId, wish.id, wish.priority - 1)),
    },
  ];

  const wishActions: ContextAction<VisitWish>[] = [
    {
      icon: <LockOutlined color="inherit" />,
      iconColor: 'inherit',
      tooltipContent: 'Hold a table exclusively',
      tooltipContentTranslation: 'reservations',
      showCondition: (wish) => !!settings?.exclusiveOffers && wish.status === 'active' && !wish.exclusive,
      onClick: (wish) => {
        if (stillCurrent()) setMarkingExclusive({ scope, wish });
      },
    },
    {
      icon: <LockOpen color="inherit" />,
      iconColor: 'inherit',
      tooltipContent: 'Remove exclusive marking',
      tooltipContentTranslation: 'reservations',
      showCondition: (wish) => !!wish.exclusive && !liveOffer(wish),
      onClick: (wish) => runAndReload(wish, () => visitWishApi.unmarkExclusive(restaurantId, wish.id)),
    },
    {
      icon: <RemoveCircleOutline color="inherit" />,
      iconColor: 'inherit',
      tooltipContent: 'Withdraw offer and free the table',
      tooltipContentTranslation: 'reservations',
      showCondition: (wish) => !!liveOffer(wish),
      onClick: (wish) => {
        if (!stillCurrent()) return;
        confirm({
          title: 'Withdraw the offer?',
          titleTranslation: 'reservations',
          description: 'The held table is released at once. The wish stays active.',
          descriptionTranslation: 'reservations',
          severity: 'warning',
          onSubmit: () => runAndReload(wish, () => visitWishApi.withdrawOffer(restaurantId, liveOffer(wish)!.id)),
        });
      },
    },
    {
      icon: <CancelOutlined color="inherit" />,
      iconColor: 'inherit',
      tooltipContent: 'Stop request',
      tooltipContentTranslation: 'reservations',
      showCondition: (wish) => OPEN_STATUSES.includes(wish.status),
      onClick: (wish) => {
        if (!stillCurrent()) return;
        confirm({
          title: 'Stop this visit wish?',
          titleTranslation: 'reservations',
          description: 'The guest gets no further notifications for this wish.',
          descriptionTranslation: 'reservations',
          severity: 'danger',
          onSubmit: () => run(wish, () => visitWishApi.stop(restaurantId, wish.id)),
        });
      },
    },
  ];

  const markedWish = enabled && markingExclusive?.scope === scope ? markingExclusive.wish : null;
  const modal = (
    <ExclusiveModal
      // A fresh dialog per wish: the duration starts at the default again.
      key={markedWish?.id ?? 'none'}
      wish={markedWish}
      onClose={() => { if (stillCurrent()) setMarkingExclusive(null); }}
      onConfirm={async (minutes) => {
        if (!markedWish || !stillCurrent()) return;
        setMarkingExclusive(null);
        await runAndReload(markedWish, () => visitWishApi.markExclusive(restaurantId, markedWish.id, minutes));
      }}
    />
  );

  return { actions: enabled ? [...priorityActions, ...wishActions] : [], priorityActions: enabled ? priorityActions : [], wishActions: enabled ? wishActions : [], busyId: busy?.scope === scope ? busy.wishId : null, modal };
}
